/**
 * Asking, once.
 *
 * `.env.example` has said since Phase 1 that product analytics is opt-in per user and that
 * enabling the client "does not consent for them". This is the asking, and three decisions in it
 * are about not being the kind of banner people have learned to dismiss without reading.
 *
 * **It says what is collected, in the sentence itself.** Not a link to a policy: "which screens
 * you reach and how long things take, never what your maps say" is short enough to read and
 * specific enough to disagree with. A banner that only says "we use cookies" is asking somebody
 * to consent to a mystery.
 *
 * **Both answers are one press, and neither is bigger than the other.** A "no" that takes two
 * clicks and lives behind "manage preferences" is a "yes" collected by attrition.
 *
 * **It never comes back.** Declining is remembered exactly as loudly as accepting, so this is
 * asked once per browser rather than once per visit.
 *
 * It is deliberately not a modal: nothing about it should stop somebody making a map, and a
 * question that blocks the product is a question answered at random.
 *
 * **And it lives on the library, not over the editor.** It was mounted at the application's root
 * for about an hour, until the responsive acceptance suite pressed "Add" on a 375px phone and
 * could not: `fixed bottom-3` sits exactly on top of the editor's bottom bar. A banner that
 * covers the product's primary control is worse than one that is never seen.
 */
import { useState } from 'react';

import { shouldAsk } from '@orbit-mind/telemetry';

import { Button } from '../ui/controls';

import { currentConsent, recordConsent } from './telemetry';

export function ConsentBanner(): React.JSX.Element | null {
  // Read once, at mount: the answer cannot change without this component being the thing that
  // changed it.
  const [asking, setAsking] = useState(() => shouldAsk(currentConsent()));

  if (!asking) return null;

  const answer = (granted: boolean): void => {
    recordConsent(granted ? 'granted' : 'declined');
    setAsking(false);
  };

  return (
    <div
      role="region"
      aria-label="Analytics"
      className="rounded-panel border-edge bg-surface animate-rise-in fixed inset-x-3 bottom-3 z-40 flex flex-wrap items-center gap-3 border p-4 shadow-lg sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-lg"
    >
      <p className="text-ink-muted min-w-0 flex-1 text-sm">
        May we count how the product is used — which screens you reach and how long things take?
        Never what your maps say.
      </p>
      <div className="flex shrink-0 gap-2">
        <Button
          onClick={() => {
            answer(false);
          }}
        >
          No
        </Button>
        <Button
          tone="primary"
          onClick={() => {
            answer(true);
          }}
        >
          Yes
        </Button>
      </div>
    </div>
  );
}
